import { UI, VStack, HStack } from '@genial/components';
import { Color, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Box, IconButton } from 'native-base';
import React from 'react';

import {
  TableContent,
  TableContentItemColumn,
  TableContentItemText,
} from '../../../../../../components/TableContent';
import { TableHeadContainer, TableHeadItem } from '../../../../../../components/TableHead';
import { NormalizedDistributionProduct } from '../../../../../../store/managementAnalysis/types';
import { FixedIncomeProductsQueryData } from '../../../../../../store/managementAnalysisDetails/selector';
import { LateralProductBar } from '../../LateralProductBar';

import { tableHead, tableHeadPlus } from './constants';

interface ProductSearchProps {
  item: Utils.ArrayElement<NonNullable<FixedIncomeProductsQueryData>>;
  product: NormalizedDistributionProduct;
}

const ProductSearchItem = ({ product, item }: ProductSearchProps) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const getFlex = (index: number) => {
    const currentHead = tableHead.find((_, i) => i === index);
    return currentHead?.flex || 1;
  };

  const getFlexPlus = (index: number) => {
    const currentHead = tableHeadPlus.find((_, i) => i === index);
    return currentHead?.flex || 1;
  };

  const renderSecondaryInfo = () => {
    return (
      <VStack space={Spacing['stack.xxxs']}>
        <Box w="full" h="1px" backgroundColor={Color['neutral.lightest']} />
        <TableHeadContainer px={Spacing['inline.xxxs']} display="flex">
          {tableHeadPlus.map((tableItem, index) => (
            <TableHeadItem key={`${tableItem.title}-${index}`} {...tableItem} />
          ))}
        </TableHeadContainer>
        <TableContent
          direction="row"
          space={0}
          py={Spacing['stack.xxs']}
          px={Spacing['inline.xxxs']}>
          <TableContentItemColumn flex={getFlexPlus(0)}>
            <TableContentItemText>{item.positionDateNormalized}</TableContentItemText>
          </TableContentItemColumn>
          <TableContentItemColumn flex={getFlexPlus(1)}>
            <VStack>
              <TableContentItemText>{item.purchaseDateNormalized}</TableContentItemText>
              <TableContentItemText>
                {item.purchaseDaysRemainingNormalized} dia(s) a decorrer
              </TableContentItemText>
            </VStack>
          </TableContentItemColumn>
          <TableContentItemColumn flex={getFlexPlus(2)}>
            <TableContentItemText>{item.purchaseValueNormalized}</TableContentItemText>
          </TableContentItemColumn>
          <TableContentItemColumn flex={getFlexPlus(3)}>
            <TableContentItemText>{item.iofValueNormalized}</TableContentItemText>
          </TableContentItemColumn>
          <TableContentItemColumn flex={getFlexPlus(4)}>
            <TableContentItemText>{item.irValueNormalized}</TableContentItemText>
          </TableContentItemColumn>
          <TableContentItemColumn flex={getFlexPlus(5)}>
            <TableContentItemText>{item.netValueNormalized}</TableContentItemText>
          </TableContentItemColumn>
        </TableContent>
      </VStack>
    );
  };

  return (
    <TableContent direction="row" space={0} py={Spacing['stack.xxs']} px={Spacing['inline.xxxs']}>
      <LateralProductBar backgroundColor={Color[product.color]} />
      <VStack w="full">
        <HStack pb={isOpen ? Spacing['inset.xs'] : 0} w="full" alignItems="center">
          <TableContentItemColumn flex={getFlex(0)}>
            <TableContentItemText>{item.customerNormalized}</TableContentItemText>
          </TableContentItemColumn>
          <TableContentItemColumn flex={getFlex(1)}>
            <TableContentItemText>{item.issuerNormalized}</TableContentItemText>
          </TableContentItemColumn>
          <TableContentItemColumn flex={getFlex(2)}>
            <TableContentItemText>{item.assetNormalized}</TableContentItemText>
          </TableContentItemColumn>
          <TableContentItemColumn flex={getFlex(3)}>
            <TableContentItemText>{item.formattedIndexRateNormalized}</TableContentItemText>
          </TableContentItemColumn>
          <TableContentItemColumn flex={getFlex(4)}>
            <TableContentItemText>{item.indexNormalized}</TableContentItemText>
          </TableContentItemColumn>
          <TableContentItemColumn flex={getFlex(5)}>
            <TableContentItemText>{item.availableQuantity.toString()}</TableContentItemText>
          </TableContentItemColumn>
          <TableContentItemColumn flex={getFlex(6)}>
            <TableContentItemText>{item.blockedQuantityNormalized.toString()}</TableContentItemText>
          </TableContentItemColumn>
          <TableContentItemColumn flex={getFlex(7)}>
            <TableContentItemText>{item.totalQuantityNormalized.toString()}</TableContentItemText>
          </TableContentItemColumn>
          <TableContentItemColumn flex={getFlex(8)}>
            <VStack>
              <TableContentItemText>{item.maturityDateNormalized}</TableContentItemText>
              <TableContentItemText>
                {item.maturityDaysRemainingNormalized} dia(s) a decorrer
              </TableContentItemText>
            </VStack>
          </TableContentItemColumn>
          <TableContentItemColumn flex={getFlex(9)}>
            <HStack justifyContent="space-between">
              <TableContentItemText noOfLines={1}>{item.grossValueNormalized}</TableContentItemText>
              <IconButton
                p={0}
                pl={Spacing['inline.nano']}
                variant="unstyled"
                onPress={() => setIsOpen(!isOpen)}
                icon={
                  <UI.Icon
                    testId="accordion-chevron-icon"
                    name={isOpen ? 'ChevronUp' : 'ChevronDown'}
                    size={'xxs'}
                    color={Color['neutral.black']}
                  />
                }
              />
            </HStack>
          </TableContentItemColumn>
        </HStack>
        {!!isOpen && renderSecondaryInfo()}
      </VStack>
    </TableContent>
  );
};

export default React.memo(ProductSearchItem);
